import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BookOpen,
  Clock,
  FolderTree,
  FlaskConical,
  Layers,
  Lock,
  Network,
  Sparkles,
} from "lucide-react";
import TopologyPreviewModal from "./topology/TopologyPreviewModal";
// MULTIVM-DUMMY-TOPOLOGY: temporary "coming soon" topology button for Multi-VM labs
import MultiVmDummyTopology from "./topology/MultiVmDummyTopology";

function DifficultyGaugeIcon({ difficulty, className = "" }) {
  const normalized = (difficulty || "").trim().toLowerCase();

  const level =
    normalized === "easy" || normalized === "low"
      ? "low"
      : normalized === "intermediate" || normalized === "medium"
        ? "medium"
        : normalized === "advanced" ||
            normalized === "hard" ||
            normalized === "high"
          ? "high"
          : null;

  if (!level) {
    return (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={className}
        fill="none"
      >
        <path
          d="M4 16a8 8 0 0 1 16 0"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.35"
        />
        <circle cx="12" cy="16" r="2" fill="currentColor" opacity="0.55" />
      </svg>
    );
  }

  const levelStyles = {
    low: {
      arcColor: "#34d399",
      needleRotation: "rotate(-35 12 16)",
    },
    medium: {
      arcColor: "#facc15",
      needleRotation: "rotate(0 12 16)",
    },
    high: {
      arcColor: "#ef4444",
      needleRotation: "rotate(35 12 16)",
    },
  };

  const { arcColor, needleRotation } = levelStyles[level];

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      fill="none"
    >
      <path
        d="M4 16a8 8 0 0 1 16 0"
        stroke="#d1d5db"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M4 16a8 8 0 0 1 16 0"
        stroke={arcColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="14 50"
      />
      <path
        d="M12 16L18 11"
        stroke="#9ca3af"
        strokeWidth="1.8"
        strokeLinecap="round"
        transform={needleRotation}
      />
      <circle cx="12" cy="16" r="2.2" fill="#9ca3af" />
      <circle cx="12" cy="16" r="0.95" fill="#f8fafc" />
    </svg>
  );
}

function splitTitleOnTopLevelSeparator(title) {
  const safeTitle = title || "";
  let depth = 0;

  for (let i = 0; i < safeTitle.length - 2; i += 1) {
    const char = safeTitle[i];

    if (char === "(") {
      depth += 1;
      continue;
    }

    if (char === ")") {
      depth = Math.max(0, depth - 1);
      continue;
    }

    if (depth === 0 && safeTitle.slice(i, i + 3) === " - ") {
      return [safeTitle.slice(0, i), safeTitle.slice(i + 3)];
    }
  }

  return [safeTitle];
}

const nodeImages = {
  "build-your-own": "/assets/tracks/build_your_own.png",
  "scywf-job-role-training": "/assets/tracks/scywf.png",
  "emerging-threats": "/assets/tracks/emerging_threats.png",
  "ai": "/assets/tracks/ai.png",
  "grc": "/assets/tracks/grc.png",
  "ot": "/assets/tracks/ot.png",
  "quantum": "/assets/tracks/quantum.png",
};

const getCardImage = (node) => {
  const slug = (node.slug || node.id || "").toLowerCase();
  const title = (node.title || "").toLowerCase();
  
  if (slug.includes("build-your-own") || title.includes("build your own")) {
    return "/assets/tracks/build_your_own.png";
  }
  if (slug.includes("scywf") || title.includes("scywf")) {
    return "/assets/tracks/scywf.png";
  }
  if (slug.includes("emerging-threats") || title.includes("emerging threats")) {
    return "/assets/tracks/emerging_threats.png";
  }
  if (slug.includes("ai-") || slug === "ai" || title.includes("artificial intelligence") || title.includes(" ai ")) {
    return "/assets/tracks/ai.png";
  }
  if (slug.includes("grc") || title.includes("grc") || title.includes("governance")) {
    return "/assets/tracks/grc.png";
  }
  if (slug.includes("ot") || title.includes("ot ") || title.includes("operational technology")) {
    return "/assets/tracks/ot.png";
  }
  if (slug.includes("quantum") || title.includes("quantum")) {
    return "/assets/tracks/quantum.png";
  }
  
  return "/assets/tracks/build_your_own.png";
};

export default function NodeCard({ node, inMultiVmCategory = false }) {
  const navigate = useNavigate();
  const [showTopologyPreview, setShowTopologyPreview] = useState(false);

  const handleClick = () => {
    if (isPendingScenario) return; // Prevent navigation for pending scenarios
    const targetId = node.slug || node.id;
    if (isLab) {
      navigate(`/lab/${targetId}`, {
        state: {
          title: node.title,
          cve: node.cve,
          description: node.description,
          difficulty: node.difficulty,
          time_estimate: node.time_estimate,
          learning_objectives: node.learning_objectives,
          // Carry the saved topology so the Lab page can show the Topology tab
          // instantly on navigation, before the catalog tree finishes loading.
          topology_json: node.topology_json,
        },
      });
      return;
    }

    navigate(`/boxes/${targetId}`);
  };

  const isLab = node.type === "scenario";
  const isCategory = node.type === "category";
  const isPendingScenario = node.type === "pending_scenario";
  const isMultiVmCategory =
    isCategory &&
    String(node.title || "")
      .trim()
      .toLowerCase()
      .includes("multi-vm");
  const childCount = node.children?.length || 0;
  const titleParts = splitTitleOnTopLevelSeparator(node.title);
  const headline = titleParts[0] || node.title || "Untitled";
  const description = isPendingScenario
    ? `This scenario is currently being generated. Status: ${node.state || "Pending"}. Check back soon!`
    : !isCategory && node.description
      ? node.description
      : titleParts.length > 1
        ? titleParts.slice(1).join(" - ")
        : isCategory
          ? "Comprehensive cybersecurity training designed to build foundational and advanced skills through hands-on labs, interactive challenges, and real-world scenarios."
          : "Hands-on scenario to practice real-world cybersecurity skills.";
  const badgeLabel = isCategory
    ? `${childCount} MODULES`
    : isPendingScenario
      ? "PENDING"
      : isLab
        ? "BOX"
        : "PATH";
  const hasTopology = isLab && !!node.topology_json;
  const topologyNodes = isLab ? node.topology_json?.nodes || [] : [];
  const topologyEdges = isLab ? node.topology_json?.edges || [] : [];
  const difficulty = isLab ? node.difficulty || null : null;
  const timeEstimate = isLab ? node.time_estimate || null : null;
  const learningObjectives = (() => {
    if (!isLab || !node.learning_objectives) return null;
    if (
      Array.isArray(node.learning_objectives) &&
      node.learning_objectives.length > 0
    )
      return node.learning_objectives;
    if (
      typeof node.learning_objectives === "string" &&
      node.learning_objectives.trim()
    )
      return [node.learning_objectives.trim()];
    return null;
  })();
  const normalizedDifficulty = (difficulty || "").trim().toLowerCase();
  const difficultyColor =
    normalizedDifficulty === "easy" || normalizedDifficulty === "low"
      ? "text-emerald-500"
      : normalizedDifficulty === "intermediate" ||
          normalizedDifficulty === "medium"
        ? "text-amber-400"
        : normalizedDifficulty === "advanced" ||
            normalizedDifficulty === "hard" ||
            normalizedDifficulty === "high"
          ? "text-rose-500"
          : "text-(--color-text-muted)";
  const ctaLabel = isLab
    ? "Go to Box"
    : isCategory
      ? "Explore Modules"
      : "Explore";

  const handleOpenTopologyPreview = (event) => {
    event?.stopPropagation();
    setShowTopologyPreview(true);
  };

  const handleCloseTopologyPreview = () => {
    setShowTopologyPreview(false);
  };

  const icon = useMemo(() => {
    if (isPendingScenario)
      return (
        <Clock className="h-5 w-5 text-gray-400 animate-pulse" />
      );
    if (isLab)
      return <FlaskConical className="h-5 w-5 text-(--color-primary)" />;
    return node.children?.length ? (
      <FolderTree className="h-5 w-5 text-(--color-primary)" />
    ) : (
      <Layers className="h-5 w-5 text-(--color-primary)" />
    );
  }, [isPendingScenario, isLab, node.children?.length]);

  return (
    <div
      role="button"
      tabIndex={isPendingScenario ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={`relative w-full h-[280px] rounded-2xl text-left overflow-hidden flex flex-col justify-between transition-all duration-300 group qv-card border border-slate-800/40 hover:border-(--color-primary)/50 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30
        ${isPendingScenario ? "opacity-60 cursor-not-allowed bg-slate-900/40" : "cursor-pointer hover:shadow-[0_0_25px_rgba(0,255,0,0.15)]"}
      `}
    >
      {/* Background Cover Image with Scale effect */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-750 ease-out group-hover:scale-105"
        style={{
          backgroundImage: `url('${getCardImage(node)}')`,
        }}
      />
      
      {/* Default Overlay - Dark gradient to shade the bottom and top */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/70 to-slate-950/50 z-0" />

      {/* Lock overlay for pending scenarios */}
      {isPendingScenario && (
        <div className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full z-10 bg-slate-950/50 border border-slate-800/50">
          <Lock className="h-4 w-4 text-gray-400" />
        </div>
      )}

      {/* RESTING CARD CONTENT (Z-10) */}
      <div className="w-full h-full p-5 flex flex-col justify-between z-10 relative pointer-events-none">
        {/* Top bar: Icon and Badge */}
        <div className="flex items-center justify-between">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-700/50 bg-slate-950/70 backdrop-blur-xs"
          >
            {icon}
          </div>
          <span className="inline-flex items-center gap-2 text-[10px] qv-cyber uppercase font-bold tracking-wider text-slate-300 bg-slate-950/65 px-2.5 py-1 rounded-md border border-slate-800/50">
            {badgeLabel}
          </span>
        </div>

        {/* Bottom bar: Title and Hover cue */}
        <div className="space-y-1">
          <h3
            className={`text-base md:text-lg font-bold text-white tracking-tight leading-snug drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] ${
              isPendingScenario ? "line-clamp-2" : ""
            }`}
          >
            {headline}
          </h3>
          <div className="text-[10px] text-slate-400 font-medium uppercase tracking-wider flex items-center gap-1.5">
            <span>Hover for details</span>
            <ArrowRight size={10} className="text-(--color-primary) animate-pulse" />
          </div>
        </div>
      </div>

      {/* HOVER-REVEAL SLIDE-UP GLASS OVERLAY (Z-20) */}
      <div 
        className={`absolute inset-0 bg-slate-950/95 backdrop-blur-md p-5 flex flex-col justify-between opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 ease-out z-20
          ${isPendingScenario ? "pointer-events-none" : ""}
        `}
      >
        {/* Header: Title and badge */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <h4
              className={`text-base font-bold text-white leading-tight ${
                isPendingScenario ? "line-clamp-2" : ""
              }`}
            >
              {headline}
            </h4>
            {isMultiVmCategory && (
              <span className="inline-flex items-center gap-1 rounded-full border border-violet-400/50 bg-violet-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-300">
                <Sparkles className="h-2.5 w-2.5" />
                Beta
              </span>
            )}
          </div>
          <span className="inline-flex items-center text-[10px] qv-cyber uppercase font-bold tracking-wider text-(--color-primary) border border-(--color-primary)/20 bg-(--color-primary)/10 px-2.5 py-0.5 rounded">
            {badgeLabel}
          </span>
        </div>

        {/* Middle content: Description & objectives/bullet list */}
        <div className="flex-1 my-3 flex flex-col justify-center gap-3 overflow-hidden">
          <p 
            className="text-[11px] text-slate-300 leading-relaxed"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </p>

          {isCategory && (
            <div className="space-y-1 text-[11px] text-slate-400">
              <div className="flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5 text-(--color-primary) shrink-0" />
                <span>Beginner to advanced pathways</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Award className="h-3.5 w-3.5 text-(--color-primary) shrink-0" />
                <span>Achievements & certifications</span>
              </div>
            </div>
          )}

          {isLab && (
            <div className="space-y-1 flex-1 overflow-hidden">
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                Learning Objectives
              </p>
              {learningObjectives ? (
                <ul className="space-y-0.5">
                  {learningObjectives.slice(0, 2).map((obj, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-300">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-(--color-primary)" />
                      <span className="line-clamp-1">{obj}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-slate-500">TBD</p>
              )}
            </div>
          )}
        </div>

        {/* Footer: Button and difficulty gauge/topology */}
        <div className="flex items-center justify-between gap-3 mt-auto pt-2 border-t border-slate-800/40">
          {isLab ? (
            <div className="flex items-center gap-3 text-[11px]">
              <span className={`inline-flex items-center gap-1 font-semibold ${difficulty ? difficultyColor : "text-slate-400"}`}>
                <DifficultyGaugeIcon difficulty={difficulty} className="h-4.5 w-4.5 shrink-0" />
                {difficulty ?? "TBD"}
              </span>
              <span className="inline-flex items-center gap-1 text-slate-400">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                {timeEstimate ?? "TBD"}
              </span>
            </div>
          ) : (
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold flex items-center gap-1">
              <span>{childCount} SUB-ITEMS</span>
            </div>
          )}

          <div className="flex items-center gap-2">
            {hasTopology && (
              <button
                type="button"
                className="cursor-pointer inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-700 bg-slate-900 text-slate-300 hover:text-(--color-primary) hover:border-(--color-primary) transition"
                onClick={handleOpenTopologyPreview}
                title="Preview Topology"
              >
                <Network className="h-4.5 w-4.5" />
              </button>
            )}
            {/* MULTIVM-DUMMY-TOPOLOGY: show placeholder topology button for Multi-VM labs without a real topology */}
            {isLab && inMultiVmCategory && !hasTopology && (
              <MultiVmDummyTopology title={headline} />
            )}
            <button
              type="button"
              disabled={isPendingScenario}
              className="inline-flex items-center gap-1 rounded-lg border border-(--color-primary) bg-(--color-primary) px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-(--color-text-on-primary) hover:opacity-90 transition cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              {isPendingScenario ? "Coming Soon" : ctaLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <TopologyPreviewModal
        show={showTopologyPreview}
        title={headline}
        nodes={topologyNodes}
        edges={topologyEdges}
        onClose={handleCloseTopologyPreview}
        showInfo={false}
      />
    </div>
  );
}
